import { useMemo } from 'react';
import type { FC } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useI18n } from '@/i18n';
import { useInstanceConfig } from '@/config/InstanceConfigProvider';
import {
    BookOpen02,
    CheckDone01,
    Grid02,
    HomeSmile,
    LineChartUp01,
    Users01,
    ZapFast,
} from '@untitledui/icons';
import type { NavigationItem } from '@/config/instance-config';
import type { TranslationKey } from '@/i18n/types';

/** Map icon name strings from config to actual icon components. */
const ICON_MAP: Record<string, FC<{ className?: string }>> = {
    HomeSmile,
    BookOpen02,
    CheckDone01,
    Grid02,
    LineChartUp01,
    Users01,
    ZapFast,
};

export interface ResolvedNavItem {
    label: string;
    href: string;
    icon: FC<{ className?: string }>;
    disabled?: boolean;
}

function resolveNavItems(configItems: NavigationItem[], t: (key: TranslationKey) => string): ResolvedNavItem[] {
    return configItems.map((item) => ({
        label: t(item.labelKey as TranslationKey),
        href: item.href,
        icon: ICON_MAP[item.iconName] ?? HomeSmile,
        disabled: item.disabled,
    }));
}

/**
 * Shared nav resolution for the app layouts. Returns the role-aware main + footer
 * nav items. Both AppLayout (slim sidebar) and AppLayoutExpanded (simple sidebar)
 * consume this so the icon map / role gating / memoisation live in one place.
 */
export function useLayoutNav(): { items: ResolvedNavItem[]; footerItems: ResolvedNavItem[] } {
    const { user, isLoading } = useAuth();
    const { t } = useI18n();
    const config = useInstanceConfig();
    const isAdmin = user?.groups?.includes('admin') || false;

    // Show admin items while loading (hidden once auth resolves to non-admin).
    const items = useMemo(() => {
        const navConfig = isLoading || isAdmin
            ? config.navigation.adminItems
            : config.navigation.operatorItems;
        return resolveNavItems(navConfig, t);
    }, [isLoading, isAdmin, config, t]);

    // Footer (e.g. Team) only when confirmed admin — never during loading.
    const footerItems = useMemo(() => {
        if (isLoading || !isAdmin) return [];
        return resolveNavItems(config.navigation.footerItems, t);
    }, [isLoading, isAdmin, config, t]);

    return { items, footerItems };
}
